'use client';

/**
 * Chat input component with send button and file upload support
 */

import React, { useState, KeyboardEvent, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface ChatInputProps {
  onSend: (message: string, files?: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  onActivity?: () => void;
  /**
   * OPT-IN: render the file attachment affordances (📎 button, drag/drop,
   * file previews). Default false — the shared widget is used by ~22 apps and
   * only the host that explicitly enables this gets file upload. Do NOT default
   * to true.
   */
  enableFileUpload?: boolean;
}

export function ChatInput({
  onSend,
  disabled = false,
  placeholder = 'Type your message...',
  autoFocus = false,
  onActivity,
  enableFileUpload = false,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showFormatting, setShowFormatting] = useState(false);
  const [showPreview, setShowPreview] = useState(true); // Show preview by default
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = async () => {
    if ((!message.trim() && selectedFiles.length === 0) || disabled || isSending) {
      return;
    }

    const messageToSend = message.trim();
    const filesToSend = selectedFiles.length > 0 ? [...selectedFiles] : undefined;
    setMessage('');
    setSelectedFiles([]);
    setIsSending(true);

    try {
      await onSend(messageToSend, filesToSend);
    } finally {
      setIsSending(false);
    }
  };

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const maxFileSize = 10 * 1024 * 1024; // 10MB
    const maxFiles = 5;
    const newFiles: File[] = [];

    for (let i = 0; i < Math.min(files.length, maxFiles - selectedFiles.length); i++) {
      const file = files[i];
      if (file.size > maxFileSize) {
        alert(`File "${file.name}" exceeds maximum size of 10MB`);
        continue;
      }
      newFiles.push(file);
    }

    if (newFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...newFiles]);
      onActivity?.();
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFiles.length, onActivity]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  }, [handleFileSelect]);

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    onActivity?.();
  }, [onActivity]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!enableFileUpload) return;
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled, enableFileUpload]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    if (!enableFileUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, [enableFileUpload]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    if (!enableFileUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  }, [disabled, enableFileUpload, handleFileSelect]);

  const handleAttachmentClick = useCallback(() => {
    if (disabled || isSending) return;
    fileInputRef.current?.click();
  }, [disabled, isSending]);

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Insert markdown formatting at cursor position
   */
  const insertMarkdown = useCallback((before: string, after: string = '', placeholder: string = 'text') => {
    const textarea = textareaRef.current;
    if (!textarea || disabled || isSending) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = message.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newMessage =
      message.substring(0, start) +
      before + textToInsert + after +
      message.substring(end);

    setMessage(newMessage);
    onActivity?.();

    // Restore cursor position after state update
    setTimeout(() => {
      if (textarea) {
        const newCursorPos = start + before.length + textToInsert.length + after.length;
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [message, disabled, isSending, onActivity]);

  const handleFormatBold = useCallback(() => {
    insertMarkdown('**', '**', 'bold text');
  }, [insertMarkdown]);

  const handleFormatItalic = useCallback(() => {
    insertMarkdown('*', '*', 'italic text');
  }, [insertMarkdown]);

  const handleFormatCode = useCallback(() => {
    insertMarkdown('`', '`', 'code');
  }, [insertMarkdown]);

  const handleFormatLink = useCallback(() => {
    insertMarkdown('[', '](url)', 'link text');
  }, [insertMarkdown]);

  const handleFormatList = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || disabled || isSending) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const lines = message.split('\n');
    let currentLine = 0;
    let charCount = 0;

    // Find which line the cursor is on
    for (let i = 0; i < lines.length; i++) {
      if (charCount + lines[i].length >= start) {
        currentLine = i;
        break;
      }
      charCount += lines[i].length + 1; // +1 for newline
    }

    // Toggle list formatting for current line
    const line = lines[currentLine];
    const isListItem = line.trim().startsWith('- ') || line.trim().startsWith('* ');
    
    if (isListItem) {
      // Remove list formatting
      lines[currentLine] = line.replace(/^(\s*)[-*]\s+/, '$1');
    } else {
      // Add list formatting
      const indent = line.match(/^(\s*)/)?.[1] || '';
      lines[currentLine] = indent + '- ' + line.trim();
    }

    const newMessage = lines.join('\n');
    setMessage(newMessage);
    onActivity?.();

    // Restore cursor position
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newPos = Math.min(start, newMessage.length);
        textarea.setSelectionRange(newPos, newPos);
      }
    }, 0);
  }, [message, disabled, isSending, onActivity]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Keyboard shortcuts for formatting
    if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
      if (e.key === 'b') {
        e.preventDefault();
        handleFormatBold();
        return;
      }
      if (e.key === 'i') {
        e.preventDefault();
        handleFormatItalic();
        return;
      }
    }
  }, [handleFormatBold, handleFormatItalic]);

  // Auto-focus when component mounts or autoFocus prop changes
  useEffect(() => {
    if (autoFocus && textareaRef.current && !disabled) {
      // Small delay to ensure the component is fully rendered
      const timeoutId = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [autoFocus, disabled]);

  // Auto-resize textarea based on content (more multiline-friendly)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200); // Max 200px for better multiline support
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [message]);

  // Get markdown components for preview (simpler styling for preview)
  const previewMarkdownComponents = useMemo<Partial<Components>>(() => {
    return {
      p: ({ children }) => (
        <p style={{ margin: '0 0 6px 0', fontSize: '14px', lineHeight: '1.5' }}>
          {children}
        </p>
      ),
      h1: ({ children }) => (
        <h1 style={{ margin: '0 0 6px 0', fontSize: '18px', fontWeight: 600 }}>
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 600 }}>
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: 600 }}>
          {children}
        </h3>
      ),
      ul: ({ children }) => (
        <ul style={{ margin: '0 0 6px 0', paddingLeft: '20px', listStyleType: 'disc' }}>
          {children}
        </ul>
      ),
      ol: ({ children }) => (
        <ol style={{ margin: '0 0 6px 0', paddingLeft: '20px', listStyleType: 'decimal' }}>
          {children}
        </ol>
      ),
      li: ({ children }) => (
        <li style={{ margin: '2px 0' }}>
          {children}
        </li>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#3b82f6',
            textDecoration: 'underline',
          }}
        >
          {children}
        </a>
      ),
      code: ({ children, className }) => {
        const isInline = !className;
        return (
          <code
            style={{
              backgroundColor: '#f3f4f6',
              padding: isInline ? '2px 4px' : '6px',
              borderRadius: '4px',
              fontSize: isInline ? '13px' : '12px',
              fontFamily: 'monospace',
              display: isInline ? 'inline' : 'block',
              margin: isInline ? '0' : '4px 0',
            }}
          >
            {children}
          </code>
        );
      },
      pre: ({ children }) => (
        <pre
          style={{
            margin: '4px 0',
            padding: '8px',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
            overflow: 'auto',
          }}
        >
          {children}
        </pre>
      ),
      blockquote: ({ children }) => (
        <blockquote
          style={{
            margin: '4px 0',
            paddingLeft: '12px',
            borderLeft: '3px solid #d1d5db',
            opacity: 0.9,
          }}
        >
          {children}
        </blockquote>
      ),
      strong: ({ children }) => (
        <strong style={{ fontWeight: 600 }}>
          {children}
        </strong>
      ),
      em: ({ children }) => (
        <em style={{ fontStyle: 'italic' }}>
          {children}
        </em>
      ),
      hr: () => (
        <hr
          style={{
            margin: '6px 0',
            border: 'none',
            borderTop: '1px solid #e5e7eb',
          }}
        />
      ),
      table: ({ children }) => (
        <div style={{ overflowX: 'auto', margin: '4px 0' }}>
          <table
            style={{
              borderCollapse: 'collapse',
              width: '100%',
            }}
          >
            {children}
          </table>
        </div>
      ),
      thead: ({ children }) => (
        <thead style={{ borderBottom: '2px solid #e5e7eb' }}>
          {children}
        </thead>
      ),
      tbody: ({ children }) => <tbody>{children}</tbody>,
      tr: ({ children }) => (
        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
          {children}
        </tr>
      ),
      th: ({ children }) => (
        <th
          style={{
            padding: '4px 8px',
            textAlign: 'left',
            fontWeight: 600,
          }}
        >
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td
          style={{
            padding: '4px 8px',
          }}
        >
          {children}
        </td>
      ),
    };
  }, []);

  const hasMessage = message.trim().length > 0;
  const canSend = (message.trim().length > 0 || selectedFiles.length > 0) && !disabled && !isSending;

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        ...(isDragging ? {
          backgroundColor: '#f0f9ff',
          borderTop: '2px dashed #3b82f6',
        } : {}),
      }}
    >
      {/* Formatting toolbar */}
      {showFormatting && (
        <div
          style={{
            display: 'flex',
            gap: '4px',
            padding: '8px 16px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            flexWrap: 'wrap',
          }}
        >
          <button
            onClick={handleFormatBold}
            disabled={disabled || isSending}
            style={{
              padding: '4px 8px',
              backgroundColor: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: disabled || isSending ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: 700,
              color: disabled || isSending ? '#9ca3af' : '#374151',
              minWidth: '28px',
            }}
            title="Bold (Ctrl+B)"
            aria-label="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            onClick={handleFormatItalic}
            disabled={disabled || isSending}
            style={{
              padding: '4px 8px',
              backgroundColor: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: disabled || isSending ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontStyle: 'italic',
              color: disabled || isSending ? '#9ca3af' : '#374151',
              minWidth: '28px',
            }}
            title="Italic (Ctrl+I)"
            aria-label="Italic"
          >
            I
          </button>
          <button
            onClick={handleFormatCode}
            disabled={disabled || isSending}
            style={{
              padding: '4px 8px',
              backgroundColor: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: disabled || isSending ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontFamily: 'monospace',
              color: disabled || isSending ? '#9ca3af' : '#374151',
              minWidth: '28px',
            }}
            title="Code"
            aria-label="Code"
          >
            {'</>'}
          </button>
          <button
            onClick={handleFormatLink}
            disabled={disabled || isSending}
            style={{
              padding: '4px 8px',
              backgroundColor: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: disabled || isSending ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              color: disabled || isSending ? '#9ca3af' : '#374151',
              minWidth: '28px',
            }}
            title="Link"
            aria-label="Link"
          >
            🔗
          </button>
          <button
            onClick={handleFormatList}
            disabled={disabled || isSending}
            style={{
              padding: '4px 8px',
              backgroundColor: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: disabled || isSending ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              color: disabled || isSending ? '#9ca3af' : '#374151',
              minWidth: '28px',
            }}
            title="List"
            aria-label="List"
          >
            • List
          </button>
        </div>
      )}

      {/* File previews */}
      {enableFileUpload && selectedFiles.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            padding: '8px 16px',
            borderBottom: '1px solid #e5e7eb',
            maxHeight: '120px',
            overflowY: 'auto',
          }}
        >
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 8px',
                backgroundColor: '#f3f4f6',
                borderRadius: '6px',
                fontSize: '12px',
                maxWidth: '200px',
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
                title={file.name}
              >
                {file.name}
              </span>
              <button
                onClick={() => handleRemoveFile(index)}
                disabled={disabled || isSending}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: disabled || isSending ? 'not-allowed' : 'pointer',
                  padding: '2px',
                  color: '#6b7280',
                  fontSize: '14px',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input area */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          padding: '12px 16px',
        }}
      >
        {/* Hidden file input (opt-in only) */}
        {enableFileUpload && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            aria-label="Upload files"
          />
        )}

        {/* Formatting toggle button */}
        <button
          onClick={() => setShowFormatting(!showFormatting)}
          disabled={disabled || isSending}
          style={{
            padding: '8px',
            backgroundColor: showFormatting ? '#e5e7eb' : 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: disabled || isSending ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '40px',
            color: disabled || isSending ? '#9ca3af' : '#6b7280',
          }}
          aria-label="Toggle formatting"
          title="Toggle formatting toolbar"
        >
          {showFormatting ? '✕' : 'Aa'}
        </button>

        {/* Attachment button (opt-in only) */}
        {enableFileUpload && (
          <button
            onClick={handleAttachmentClick}
            disabled={disabled || isSending}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: disabled || isSending ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '40px',
              color: disabled || isSending ? '#9ca3af' : '#6b7280',
            }}
            aria-label="Attach file"
            title="Attach file"
          >
            📎
          </button>
        )}

        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            onActivity?.();
          }}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          onFocus={() => onActivity?.()}
          placeholder={placeholder}
          disabled={disabled || isSending}
          rows={1}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'inherit',
            resize: 'none',
            minHeight: '40px',
            maxHeight: '200px',
            outline: 'none',
            lineHeight: '1.5',
            overflowY: 'auto',
          }}
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          style={{
            padding: '8px 16px',
            backgroundColor: !canSend ? '#d1d5db' : '#3b82f6',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            cursor: !canSend ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            minWidth: '60px',
            alignSelf: 'flex-end',
          }}
        >
          {isSending ? '...' : 'Send'}
        </button>
      </div>

      {/* Markdown Preview */}
      {hasMessage && (
        <div
          style={{
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            padding: '12px 16px',
            maxHeight: '200px',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Preview
            </div>
            <button
              onClick={() => setShowPreview(!showPreview)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#6b7280',
                fontSize: '14px',
              }}
              aria-label={showPreview ? 'Hide preview' : 'Show preview'}
              title={showPreview ? 'Hide preview' : 'Show preview'}
            >
              {showPreview ? '▼' : '▲'}
            </button>
          </div>
          {showPreview && (
            <div
              style={{
                padding: '12px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                lineHeight: '1.5',
                color: '#111827',
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={previewMarkdownComponents}
              >
                {message}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

